import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEditProtection } from '../../shared/components/EditProtection';
import './CashAddition.css';

interface CashHolder { id: number; name: string; total_cash: number | string; }
interface CashAdditionRequest {
  transaction_id: string;
  source: string;
  destination: string;
  recipient_user_id?: number;
  amount: string;
  description: string;
}
const sourceLabels: Record<string, string> = { cash_payment: 'Cash payment', bank_withdrawal: 'Bank withdrawal' };

export default function CashAddition() {
  const { user, sessionToken } = useAuth();
  const navigate = useNavigate();
  const canSelectUser = !!user && ['Owner', 'Admin', 'Manager'].includes(user.role);
  const [source, setSource] = useState('cash_payment');
  const [destination, setDestination] = useState('till');
  const [recipientId, setRecipientId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [users, setUsers] = useState<CashHolder[]>([]);
  const [userError, setUserError] = useState('');
  const [retryUsers, setRetryUsers] = useState(0);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<CashAdditionRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const busy = useRef(false);
  useEditProtection(!saved && (!!amount || !!description || !!pending));

  useEffect(() => {
    if (!canSelectUser || destination !== 'user') return;
    let active = true;
    setUserError('');
    fetch('/api/users', { headers: { 'X-Session-Token': sessionToken || '' }, cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load cash holders.');
        const data = await response.json();
        if (active) setUsers(data.users || []);
      })
      .catch(() => { if (active) setUserError('Unable to load cash holders. Please retry.'); });
    return () => { active = false; };
  }, [canSelectUser, destination, sessionToken, retryUsers]);

  const holder = canSelectUser ? users.find(person => person.id === Number(recipientId)) : user;
  const recipientName = destination === 'till' ? 'Till' : holder?.name || 'Choose a cash holder';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy.current || saved) return;
    setError('');
    if (!pending) {
      if (!/^\d{1,8}(\.\d{1,2})?$/.test(amount) || Number(amount) <= 0) {
        setError('Enter an amount greater than zero with no more than two decimal places.');
        return;
      }
      if (!description.trim()) { setError('Describe the cash payment or bank withdrawal.'); return; }
      if (destination === 'user' && !holder) { setError('Choose who is holding the cash.'); return; }
    }
    const request = pending || {
      transaction_id: crypto.randomUUID(), source, destination,
      recipient_user_id: destination === 'user' ? holder!.id : undefined,
      amount: Number(amount).toFixed(2), description: description.trim(),
    };
    busy.current = true;
    setSubmitting(true);
    setPending(request);
    try {
      const response = await fetch('/api/cash-addition', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify(request), cache: 'no-store',
      });
      const result = await response.json();
      if (!response.ok) {
        if ([400, 401, 403, 404].includes(response.status)) setPending(null);
        throw new Error(result.error || 'Unable to confirm the cash addition. Retry the same entry.');
      }
      setSaved(true);
      setTimeout(() => navigate('/cash', { state: { toast: { type: 'success', title: 'Cash Added', message: result.message } } }), 0);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to confirm the cash addition. Please retry.');
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div className="cash-addition">
      <button type="button" className="cash-addition__back" onClick={() => navigate('/cash')}>← Cash Management</button>
      <h2>Add Cash</h2>
      <p className="cash-addition__intro">Record cash received from a payment or withdrawn from a bank account.</p>
      <form onSubmit={submit}>
        <fieldset disabled={submitting || !!pending || saved}>
          <legend className="cash-addition__legend">Cash details</legend>
          <div className="cash-addition__grid">
            <label>Source
              <select value={source} onChange={event => setSource(event.target.value)}>
                <option value="cash_payment">Cash payment</option>
                <option value="bank_withdrawal">Bank withdrawal</option>
              </select>
            </label>
            <label>Add to
              <select value={destination} onChange={event => setDestination(event.target.value)}>
                <option value="till">Till</option>
                <option value="user">{canSelectUser ? 'User-held cash' : 'My cash'}</option>
              </select>
            </label>
          </div>
          {destination === 'user' && canSelectUser && (
            <label>Cash holder
              <select value={recipientId} onChange={event => setRecipientId(event.target.value)} required>
                <option value="">Choose a cash holder</option>
                {users.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}
              </select>
            </label>
          )}
          {destination === 'user' && userError && <div role="alert">{userError} <button type="button" onClick={() => setRetryUsers(value => value + 1)}>Retry loading users</button></div>}
          <label>Amount (CAD)
            <input type="text" inputMode="decimal" placeholder="0.00" value={amount} maxLength={11} onChange={event => setAmount(event.target.value)} required />
          </label>
          <label>{source === 'bank_withdrawal' ? 'Bank / withdrawal details' : 'Payment details'}
            <textarea value={description} onChange={event => setDescription(event.target.value)} maxLength={500} rows={3} required
              placeholder={source === 'bank_withdrawal' ? 'Bank name and withdrawal reference or purpose' : 'Who paid and what the payment was for'} />
          </label>
        </fieldset>
        <div className="cash-addition__summary" aria-label="Cash addition summary">
          <h3>Review cash addition</h3>
          <dl>
            <div><dt>Source</dt><dd>{sourceLabels[source]}</dd></div>
            <div><dt>Cash goes to</dt><dd>{recipientName}</dd></div>
            <div><dt>Amount to add</dt><dd>{new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(amount) || 0)}</dd></div>
          </dl>
          <p>Record only cash that is not already included in this balance or in Clover cash sales.</p>
        </div>
        {error && <div className="cash-addition__error" role="alert">{error}</div>}
        {pending && !submitting && !saved && <p role="status">This entry is kept unchanged until confirmed. Retry safely without adding the cash twice.</p>}
        <button className="cash-addition__submit" type="submit" disabled={submitting || saved}>{saved ? 'Cash added' : submitting ? 'Recording…' : pending ? 'Retry cash addition' : 'Add Cash'}</button>
      </form>
    </div>
  );
}
